import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth as useClerkAuth, useUser } from '@clerk/react';

// Bearer tokens only — never cookie credentials. (Wildcard CORS + credentials is
// rejected by browsers; Clerk gives us a fresh JWT to send as a Bearer header.)
axios.defaults.withCredentials = false;

// Route relative '/api/...' calls to the backend. In prod REACT_APP_BACKEND_URL
// is the backend domain (e.g. https://api.hub.henry-griffitts.com) so cross-origin
// API calls reach it; in local dev it's empty → relative paths hit the craco proxy.
// Set at module load, before the request interceptor (in useEffect) ever runs.
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();

  // Keep the latest getToken in a ref so the single global interceptor (installed
  // once below) always calls the current one without re-registering on every render.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    // One global axios request interceptor that attaches a fresh Clerk token.
    // Core 3 background-refreshes the session (~every 50s), so getToken() returns
    // an already-valid cached token — no manual refresh logic needed here.
    const id = axios.interceptors.request.use(async (config) => {
      try {
        const token = getTokenRef.current ? await getTokenRef.current() : null;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
        }
      } catch {
        // Signed out / token unavailable mid-session — send without the header
        // and let the backend respond 401. Never throw from the interceptor.
        delete config.headers.Authorization;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // Shape Clerk's user into the { email, name, picture, id } the app already expects.
  const user = useMemo(() => {
    if (!clerkUser) return null;
    const email = clerkUser.primaryEmailAddress?.emailAddress || '';
    return {
      id: clerkUser.id,
      email,
      name: clerkUser.fullName || email,
      picture: clerkUser.imageUrl || '',
    };
  }, [clerkUser]);

  const logout = async () => {
    try {
      await signOut();
    } catch {
      // ignore — Clerk handles UI state
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoaded, isSignedIn, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
